import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';

import { runSessionSwitch } from '@/api';
import { USER_DASHBOARD_QUERY_KEYS } from '@/api/user-dashboard/queries';

import { _refreshToken } from './mutations';
import type { RefreshTokenParams } from './types';

export const useIsRefreshingSession = () =>
  useIsMutating({
    predicate: (mutation) => mutation.options.scope?.id === 'session-refresh',
  }) > 0;

/**
 * Cached API responses belong to the session that fetched them. Reads under the outgoing session
 * are cancelled before it is replaced, then every active query re-fetches under the replacement.
 *
 * Resolves once the replacement session's permissions are cached, so an awaiting caller can
 * navigate without route guards still seeing the outgoing session's permissions. Rejects only
 * when the session itself was not replaced, so a caller can align its local account on success.
 */
export const useRefreshSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    scope: { id: 'session-refresh' },
    mutationFn: async (session: RefreshTokenParams) => {
      await runSessionSwitch(async () => {
        await queryClient.cancelQueries();
        await _refreshToken(session);
      });
      await queryClient.invalidateQueries({ queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile] });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== USER_DASHBOARD_QUERY_KEYS.userProfile,
      });
    },
  });
};
